import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import { Checkbox } from '../../../components/ui/Checkbox';
import { formatDateInBarbados } from '../../../utils/timezone';
import { formatAmountWithSign, getCurrencySymbol } from '../../../utils/currency';
import Button from '../../../components/ui/Button';


const TransactionsTable = ({
  transactions,
  selectedTransactions,
  onSelectionChange,
  onEdit,
  onDelete,
  onSplit,
  onBulkDelete,
  onBulkCategoryChange
}) => {
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const sortedTransactions = useMemo(() => {
    const sorted = [...transactions];
    sorted?.sort((a, b) => {
      let aVal, bVal;
      switch (sortBy) {
        case 'date':
          aVal = new Date(a?.date);
          bVal = new Date(b?.date);
          break;
        case 'description':
          aVal = a?.description?.toLowerCase();
          bVal = b?.description?.toLowerCase();
          break;
        case 'amount':
          aVal = Math.abs(a?.amount);
          bVal = Math.abs(b?.amount);
          break;
        case 'category':
          aVal = a?.category?.toLowerCase();
          bVal = b?.category?.toLowerCase();
          break;
        case 'account':
          aVal = a?.account?.toLowerCase();
          bVal = b?.account?.toLowerCase();
          break;
        default:
          return 0;
      }
      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [transactions, sortBy, sortOrder]);

  const totalPages = Math.ceil(sortedTransactions?.length / itemsPerPage);
  const paginatedTransactions = sortedTransactions?.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      onSelectionChange(paginatedTransactions?.map(t => t?.id));
    } else {
      onSelectionChange([]);
    }
  };

  const handleSelectTransaction = (transactionId, checked) => {
    if (checked) {
      onSelectionChange([...selectedTransactions, transactionId]);
    } else {
      onSelectionChange(selectedTransactions?.filter(id => id !== transactionId));
    }
  };

  const isAllSelected = paginatedTransactions?.length > 0 &&
    paginatedTransactions?.every(t => selectedTransactions?.includes(t?.id));

  const getCurrencySymbol = () => '$';

  const formatDate = (dateString) => {
    return formatDateInBarbados(dateString, {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getAmountColor = (amount) => {
    return amount >= 0 ? 'text-success' : 'text-error';
  };

  const SortIcon = ({ field }) => {
    if (sortBy !== field) return <Icon name="ChevronsUpDown" size={14} className="opacity-50" />;
    return sortOrder === 'asc'
      ? <Icon name="ChevronUp" size={14} />
      : <Icon name="ChevronDown" size={14} />;
  };

  return (
    <div className="bg-card rounded-lg shadow-elevation-2">
      <div className="p-16 md:p-20 border-b border-border">
        <div className="flex items-center justify-between">
          <h3 className="text-base md:text-lg font-heading font-semibold text-card-foreground">
            Transactions ({sortedTransactions?.length})
          </h3>
          {selectedTransactions?.length > 0 && (
            <div className="flex items-center gap-8">
              <Button
                variant="outline"
                size="sm"
                iconName="Tag"
                iconPosition="left"
                onClick={onBulkCategoryChange}
              >
                Change Category ({selectedTransactions?.length})
              </Button>
              <Button
                variant="destructive"
                size="sm"
                iconName="Trash2"
                iconPosition="left"
                onClick={onBulkDelete}
              >
                Delete Selected ({selectedTransactions?.length})
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted">
            <tr>
              <th className="px-16 py-12 text-left">
                <Checkbox
                  checked={isAllSelected}
                  onChange={(e) => handleSelectAll(e?.target?.checked)}
                />
              </th>
              <th
                className="px-16 py-12 text-left text-sm font-caption font-semibold text-foreground cursor-pointer hover:bg-muted/80"
                onClick={() => handleSort('date')}
              >
                <div className="flex items-center gap-8">
                  Date
                  <SortIcon field="date" />
                </div>
              </th>
              <th
                className="px-16 py-12 text-left text-sm font-caption font-semibold text-foreground cursor-pointer hover:bg-muted/80"
                onClick={() => handleSort('description')}
              >
                <div className="flex items-center gap-8">
                  Description
                  <SortIcon field="description" />
                </div>
              </th>
              <th
                className="px-16 py-12 text-left text-sm font-caption font-semibold text-foreground cursor-pointer hover:bg-muted/80"
                onClick={() => handleSort('account')}
              >
                <div className="flex items-center gap-8">
                  Account
                  <SortIcon field="account" />
                </div>
              </th>
              <th
                className="px-16 py-12 text-left text-sm font-caption font-semibold text-foreground cursor-pointer hover:bg-muted/80"
                onClick={() => handleSort('category')}
              >
                <div className="flex items-center gap-8">
                  Category
                  <SortIcon field="category" />
                </div>
              </th>
              <th
                className="px-16 py-12 text-right text-sm font-caption font-semibold text-foreground cursor-pointer hover:bg-muted/80"
                onClick={() => handleSort('amount')}
              >
                <div className="flex items-center justify-end gap-8">
                  Amount
                  <SortIcon field="amount" />
                </div>
              </th>
              <th className="px-16 py-12 text-right text-sm font-caption font-semibold text-foreground">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {paginatedTransactions?.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-16 py-32 text-center text-muted-foreground">
                  No transactions found
                </td>
              </tr>
            ) : (
              paginatedTransactions?.map((transaction) => (
                <tr
                  key={transaction?.id}
                  className="border-b border-border hover:bg-muted/50 transition-colors"
                >
                  <td className="px-16 py-12">
                    <Checkbox
                      checked={selectedTransactions?.includes(transaction?.id)}
                      onChange={(e) => handleSelectTransaction(transaction?.id, e?.target?.checked)}
                    />
                  </td>
                  <td className="px-16 py-12 text-sm text-foreground">
                    {formatDate(transaction?.date)}
                  </td>
                  <td className="px-16 py-12 text-sm text-foreground font-medium">
                    {transaction?.description}
                  </td>
                  <td className="px-16 py-12 text-sm text-muted-foreground">
                    {transaction?.account}
                  </td>
                  <td className="px-16 py-12">
                    <span className="inline-flex items-center px-8 py-4 rounded-full text-xs font-medium bg-primary/10 text-primary">
                      {transaction?.category}
                    </span>
                  </td>
                  <td className={`px-16 py-12 text-sm font-semibold text-right ${getAmountColor(transaction?.amount)}`}>
                    {formatAmountWithSign(transaction?.amount)}
                  </td>
                  <td className="px-16 py-12 text-right">
                    <div className="flex items-center justify-end gap-8">
                      <Button
                        variant="ghost"
                        size="icon"
                        iconName="Split"
                        onClick={() => onSplit(transaction)}
                        title="Split Transaction"
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        iconName="Edit"
                        onClick={() => onEdit(transaction)}
                        title="Edit Transaction"
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        iconName="Trash2"
                        onClick={() => onDelete(transaction?.id)}
                        title="Delete Transaction"
                      />
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden">
        {paginatedTransactions?.length === 0 ? (
          <div className="px-16 py-32 text-center text-muted-foreground">
            No transactions found
          </div>
        ) : (
          <div className="divide-y divide-border">
            {paginatedTransactions?.map((transaction) => (
              <div key={transaction?.id} className="p-16 hover:bg-muted/50 transition-colors">
                <div className="flex items-start gap-12 mb-12">
                  <Checkbox
                    checked={selectedTransactions?.includes(transaction?.id)}
                    onChange={(e) => handleSelectTransaction(transaction?.id, e?.target?.checked)}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-8 mb-8">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-foreground mb-4">
                          {transaction?.description}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {formatDate(transaction?.date)}
                        </div>
                      </div>
                      <div className={`text-sm font-semibold whitespace-nowrap ${getAmountColor(transaction?.amount)}`}>
                        {formatAmountWithSign(transaction?.amount)}
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-8 mb-12">
                      <div className="text-xs text-muted-foreground">
                        {transaction?.account}
                      </div>
                      <span className="inline-flex items-center px-8 py-4 rounded-full text-xs font-medium bg-primary/10 text-primary">
                        {transaction?.category}
                      </span>
                    </div>
                    <div className="flex items-center gap-8">
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Split"
                        onClick={() => onSplit(transaction)}
                      >
                        Split
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Edit"
                        onClick={() => onEdit(transaction)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Trash2"
                        onClick={() => onDelete(transaction?.id)}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="p-16 md:p-20 border-t border-border flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Page {currentPage} of {totalPages}
          </div>
          <div className="flex gap-8">
            <Button
              variant="outline"
              size="sm"
              iconName="ChevronLeft"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(prev => prev - 1)}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              iconName="ChevronRight"
              iconPosition="right"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(prev => prev + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransactionsTable;